import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Language } from 'src/utils/Languages';
import { SearchResults } from 'src/Components/Search';

type Api = {
    app?: AppDto;
    get: (path: string) => Promise<IPageDto>;
    loading: boolean;
    getSearchResults: (query: string, page: number) => Promise<SearchResults>;
    fetchNewsPreviews: (
        uid: string,
        start: number,
        take: number,
        category?: string,
        year?: string,
        tags?: string[]) => Promise<NewsPreviewsDto>;
    fetchDownloadItems: (
        uid: string,
        start?: number,
        take?: number,
        category?: string,
        tags?: string[]) => Promise<DownloadItemDto[]>;
    fetchProductPreviews: (
        uid: string,
        start?: number,
        take?: number,
        targetGroup?: string,
        label?: string,
        productGroup?: string,
        productType?: string,
        tagsIds?: string[]) => Promise<ProductPreviewDto[]>;
};

export const ApiContext = React.createContext<Api>({} as never);

type Props = {};
export const Api: React.FC<Props> = ({ children }) => {

    const [appDto, setAppDto] = useState<AppDto>();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [appLoading, setAppLoading] = useState(false);

    useEffect(() => {
        if (!appDto) {
            fetchApp()
                .then(app => {
                    setAppDto(app)
                })
        }
    }, []);

    const fetchPage = async (path: string): Promise<IPageDto> => {
        const requestOptions: RequestInit = {
            method: 'GET',
            headers: {
                'Accept-Language': Language,
                'Content-Type': 'application/json'
            }
        };

        setAppLoading(true);
        setPageLoading(true);

        return fetch(`${path}?json`, requestOptions)
            .then(response => {
                return response.json();
            })
            .then(pageDto => {
                setLoading(false);
                setAppLoading(pageLoading);
                return pageDto
            })
            .catch(() => {
                setLoading(false);
                setAppLoading(pageLoading);
            });
    }

    const fetchApp = async (): Promise<AppDto> => {
        const requestOptions: RequestInit = {
            method: 'GET',
            headers: {
                'Accept-Language': Language,
                'Content-Type': 'application/json'
            }
        };

        setAppLoading(true);
        setLoading(true);

        return fetch(`/${Language}/api/app`, requestOptions)
            .then(response => {
                return response
                    .json();
            })
            .then(appJson => {
                setLoading(false);
                setPageLoading(appLoading);
                return appJson;
            })
            .catch(() => {
                setLoading(false);
                setPageLoading(appLoading);
            });
    }

    const search = (query: string, page: number) => {
        const requestOptions = {
            headers: { 'Content-Type': 'application/json' },
            method: 'GET'
        };

        setLoading(true);

        return fetch(`/api/search?query=${query}&page=${page}&lang=${Language}`, requestOptions)
            .then(response => {
                return response
                    .json()
                    .then(json => {
                        setLoading(false);
                        // setAppLoading(pageLoading);
                        return json;
                    });

                // TODO: how to deal with erros?
            }).catch(result => {
                setLoading(false);
                // setAppLoading(pageLoading);
            });
    }

    const fetchNewsPreviews = async (
        uid: string,
        start: number,
        take: number,
        category?: string,
        year?: string,
        tags?: string[]): Promise<NewsPreviewsDto> => {

        let queryString = `?uid=${uid}&start=${start}&take=${take}`;

        if (category) {
            queryString += `&categoryid=${category}`;
        }

        if (year) {
            queryString += `&year=${year}`;
        }

        if (tags && tags.length > 0) {
            // TODO: Is this a good way to do it?
            const result = tags.filter(t => t);
            queryString += `&tagsids=${result}`;
        }

        const requestOptions: RequestInit = {
            method: 'GET',
            headers: {
                'Accept-Language': Language,
                'Content-Type': 'application/json'
            }
        };

        setLoading(true);

        return fetch(`/api/news${queryString}`, requestOptions)
            .then(response => {
                return response.json();
            })
            .then(result => {
                setLoading(false);
                return result
            })
            .catch((e) => {
                setLoading(false);
            });
    };

    const fetchDownloadItems = async (
        uid: string,
        start?: number,
        take?: number,
        category?: string,
        tags?: string[]): Promise<DownloadItemDto[]> => {

        let queryString = `?uid=${uid}&start=${start}&take=${take}`;

        if (category) {
            queryString += `&categoryid=${category}`;
        }

        if (tags && tags.length > 0) {
            // TODO: Is this a good way to do it?
            const result = tags.filter(t => t);
            queryString += `&tagsids=${result}`;
        }

        const requestOptions: RequestInit = {
            method: 'GET',
            headers: {
                'Accept-Language': Language,
                'Content-Type': 'application/json'
            }
        };

        setLoading(true);

        return fetch(`/api/downloads${queryString}`, requestOptions)
            .then(response => {
                return response.json();
            })
            .then(result => {
                setLoading(false);
                return result
            })
            .catch((e) => {
                setLoading(false);
            });
    };

    const fetchProductPreviews = async (
        uid: string,
        start?: number,
        take?: number,
        targetGroup?: string,
        label?: string,
        productGroup?: string,
        productType?: string,
        tagsIds?: string[]): Promise<ProductPreviewDto[]> => {

        let queryString = `?uid=${uid}&start=${start}&take=${take}`;

        if (targetGroup) {
            queryString += `&targetgroupid=${targetGroup}`;
        }

        if (label) {
            queryString += `&labelid=${label}`;
        }

        if (productGroup) {
            queryString += `&productgroupid=${productGroup}`;
        }

        if (productType) {
            queryString += `&producttypeid=${productType}`;
        }

        if (tagsIds && tagsIds.length > 0) {
            queryString += `&tagsIds=${tagsIds}`;
        }

        const requestOptions: RequestInit = {
            method: 'GET',
            headers: {
                'Accept-Language': Language,
                'Content-Type': 'application/json'
            }
        };

        setLoading(true);

        return fetch(`/api/products${queryString}`, requestOptions)
            .then(response => {
                return response.json();
            })
            .then(result => {
                setLoading(false);
                return result
            })
            .catch((e) => {
                setLoading(false);
            });
    };

    return (
        <ApiContext.Provider
            value={{
                app: appDto,
                get: fetchPage,
                loading,
                getSearchResults: search,
                fetchNewsPreviews,
                fetchProductPreviews,
                fetchDownloadItems
            }}
        >
            {children}
        </ApiContext.Provider>
    );
};

export const useApi = () => useContext(ApiContext);


// WEBPACK FOOTER //
// ./src/@context/Api.tsx